import { QueryClient } from "@tanstack/react-query";
import { useLocation } from "@tanstack/react-router";
import { useLayoutEffect, useState } from "react";
import { shouldDeferCatalogLayout } from "@/features/catalog/utils/catalogLayoutRoute";
import { usePlayerStore } from "@/features/player/stores/playerStore";
import { useSettingsStore } from "@/features/settings/hooks";
import { resolveLightMode } from "@/features/settings/stores/legacyDisplaySettings";
import { resolvePostFontFamily } from "@/features/settings/utils/renderSettings";
import { useIsDesktop, useSystemPrefersDark } from "@/shared/hooks";
import { useHasFinePointer } from "@/shared/hooks/useMediaQuery";
import { useOnlineUsersHeartbeat } from "@/shared/hooks/useOnlineUsersHeartbeat";
import { useRouteCanonical } from "@/shared/hooks/useRouteCanonical";

export interface RootPresentation {
  queryClient: QueryClient;
  privacyMode: boolean;
  miniPlayerVisible: boolean;
  isDesktop: boolean;
  hasFinePointer: boolean;
  pathname: string;
  isStandaloneJukebox: boolean;
  deferCatalogLayout: boolean;
}

export function useRootPresenter(): RootPresentation {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
          },
        },
      }),
  );
  useRenderSettings();

  const privacyMode = useSettingsStore((state) => state.privacyMode);
  const miniPlayerVisible = usePlayerStore((state) => state.miniPlayer.visible);
  const isDesktop = useIsDesktop();
  const hasFinePointer = useHasFinePointer();
  const location = useLocation();
  useRouteCanonical(location.pathname);
  useOnlineUsersHeartbeat(location.pathname !== "/jukebox");

  // standalone は小さい別ウィンドウでも通常のモバイル枠を外す。
  const isStandaloneJukebox =
    location.pathname === "/jukebox" &&
    (isDesktop ||
      (location.search as { standalone?: boolean }).standalone === true);

  return {
    queryClient,
    privacyMode,
    miniPlayerVisible,
    isDesktop,
    hasFinePointer,
    pathname: location.pathname,
    isStandaloneJukebox,
    deferCatalogLayout: shouldDeferCatalogLayout(location.pathname),
  };
}

function useRenderSettings(): void {
  const darkMode = useSettingsStore((state) => state.darkMode);
  const globalFontScale = useSettingsStore((state) => state.globalFontScale);
  const postBodyFontSize = useSettingsStore((state) => state.postBodyFontSize);
  const fontFamily = useSettingsStore((state) => state.fontFamily);
  const systemPrefersDark = useSystemPrefersDark();

  useLayoutEffect(() => {
    document.documentElement.classList.toggle(
      "light-mode",
      resolveLightMode(darkMode, systemPrefersDark),
    );
  }, [darkMode, systemPrefersDark]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      "font-size",
      `${globalFontScale / 100}rem`,
    );
  }, [globalFontScale]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      "--post-body-font-size",
      `${postBodyFontSize}px`,
    );
  }, [postBodyFontSize]);

  useLayoutEffect(() => {
    const postFontFamily = resolvePostFontFamily(fontFamily);
    if (postFontFamily === null) {
      document.documentElement.style.removeProperty("--post-font-family");
      return;
    }
    document.documentElement.style.setProperty(
      "--post-font-family",
      postFontFamily,
    );
  }, [fontFamily]);
}
